import { Collapse } from "@material-ui/core";
import React, { ReactElement, ReactNode, useContext, useEffect } from "react";
import features from "../features.json";
import { useLocalStorage } from "../services";
import { useAuth } from "./AuthContext";

export enum Permission {
  "ACTIVE" = 3,
  "OPT_OUT" = 2,
  "OPT_IN" = 1,
  "INACTIVE" = 0,
}

export interface IFeature {
  name: string;
  permission: Permission;
  featureConfig: FeatureFlag;
  active: boolean;
  parameters: FeatureParameters;
}

export type FeatureParameter = TextFeatureParameter | FlagFeatureParameter;
export interface FeatureParameters {
  [key: string]: FeatureParameter;
}

interface TextFeatureParameter {
  type: "string";
  name: string;
  default?: string;
  value?: string;
}

interface FlagFeatureParameter {
  type: "flag";
  name: string;
  default?: boolean;
  value?: boolean;
}

interface FeatureFlag {
  name: string;
  description: string;
  active: boolean;
  defaultPolicy: string | boolean | undefined;
  roles: { [roleName: string]: string | boolean | undefined };
  parameters: FeatureParameter[];
}

export interface FeatureFlagsState {
  allowedFeatures: IFeature[];
  toggleOptStatus: (name: string) => boolean | undefined;
  setParameters: (featureName: string, parameters: FeatureParameters) => void;
}

type ToggleOptStatus = (name: string) => boolean | undefined;
export interface FeatureFlagsState {
  allowedFeatures: IFeature[];
  optionalFeatures: IFeature[];
  toggleOptStatus: ToggleOptStatus;
}

export interface FeatureOptionsWrapper {
  optionalFeatures: IFeature[];
  toggleOptStatus: ToggleOptStatus;
}

const FeatureFlagsContext = React.createContext({} as FeatureFlagsState);
const rolesClaimName = `https://${process.env.REACT_APP_AUTH0_DOMAIN?.replace(
  /\/$/,
  ""
)}/roles`;

const filterAndSortPermissions = (
  permissions: (undefined | string | boolean)[]
): Permission[] =>
  permissions
    .filter((permission) => permission !== undefined)
    .map((a: any) => {
      return stringToPermission(a);
    })
    .sort((a: Permission, b: Permission) => b - a);

const getRolesBasedPermission = (
  feature: FeatureFlag,
  roles: string[]
): Permission | undefined => {
  if (feature.roles) {
    const permissions = roles.reduce((permissions, role): (string | boolean)[] => {
      const permission = feature.roles[role];
      if (permission === undefined) return permissions;
      else return [...permissions, permission];
    }, [] as any[]);
    return filterAndSortPermissions(permissions)[0];
  } else return undefined;
};

const computeActiveState = (permission: Permission): boolean => {
  switch (permission) {
    case Permission.ACTIVE:
    case Permission.OPT_OUT:
      return true;
    case Permission.OPT_IN:
    case Permission.INACTIVE:
    default:
      return false;
  }
};

export const stringToPermission = (permission: any): Permission => {
  switch (typeof permission) {
    case "boolean":
      return permission ? Permission.ACTIVE : Permission.INACTIVE;
    case "string":
      return Permission[
        permission.toUpperCase().replace("-", "_") as keyof typeof Permission
      ];
    default:
      return Permission.INACTIVE;
  }
};

const computeAllowedFeatures = (
  features: any[],
  roles: string[] | undefined
): IFeature[] => {
  const allowedFeatures = features
    .filter((feature: FeatureFlag) => feature.active)
    .reduce((allowedFeatures: IFeature[], featureConfig: FeatureFlag) => {
      const rolesBasedPermission = roles
        ? getRolesBasedPermission(featureConfig, roles)
        : undefined;
      const defaultPermission = featureConfig.defaultPolicy
        ? filterAndSortPermissions([featureConfig.defaultPolicy])[0]
        : Permission.INACTIVE;
      const permission =
        rolesBasedPermission !== undefined
          ? rolesBasedPermission
          : defaultPermission !== undefined
          ? defaultPermission
          : Permission.INACTIVE;
      return [
        ...allowedFeatures,
        {
          name: featureConfig.name,
          permission: Permission[permission],
          featureConfig,
          parameters: featureConfig.parameters?.length
            ? Object.fromEntries(
                featureConfig.parameters?.map((parameter) => [parameter.name, parameter])
              )
            : {},
          active: computeActiveState(permission),
        },
      ];
    }, [] as IFeature[]);
  return allowedFeatures;
};

const getRoles = (user: any) => (user ? user[rolesClaimName] : []);

export const FeatureFlagsProvider = ({ children }: { children: ReactElement }) => {
  const { user, isAuthenticated } = useAuth();
  const [allowedFeatures, setAllowedFeatures] = useLocalStorage<IFeature[]>(
    "features",
    []
  );

  useEffect(() => {
    if (isAuthenticated && allowedFeatures.length === 0) {
      setAllowedFeatures(computeAllowedFeatures(features, getRoles(user)));
    }
  }, [isAuthenticated, user, allowedFeatures, setAllowedFeatures]);

  const toggleOptStatus = (name: string) => {
    let active: boolean | undefined = undefined;
    const newFeatures = allowedFeatures.map((feature) => {
      if (
        feature.name === name &&
        (stringToPermission(feature.permission) === Permission.OPT_IN ||
          stringToPermission(feature.permission) === Permission.OPT_OUT)
      ) {
        active = !feature.active;
        return { ...feature, active };
      }
      return feature;
    });
    setAllowedFeatures(newFeatures);
    return active;
  };

  const setParameters = (featureName: string, parameters: FeatureParameters) =>
    setAllowedFeatures((features) =>
      features.map((feature) => {
        return feature.name === featureName
          ? {
              ...feature,
              parameters,
            }
          : feature;
      })
    );

  const optionalFeatures: IFeature[] = allowedFeatures.filter(
    ({ permission }: IFeature) =>
      stringToPermission(permission) === Permission.OPT_IN ||
      stringToPermission(permission) === Permission.OPT_OUT
  );

  return (
    <FeatureFlagsContext.Provider
      value={{ allowedFeatures, optionalFeatures, toggleOptStatus, setParameters }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatures = () => useContext(FeatureFlagsContext);

export const useFeature = (name: string): IFeature | undefined => {
  const { allowedFeatures } = useFeatures();
  return allowedFeatures.find((feature) => name === feature.name);
};

export const Feature = ({
  name,
  children,
  render = children,
  className,
}: {
  name: string;
  children?: ReactNode | ((hasFeature: boolean | Permission) => JSX.Element);
  render?: ReactNode | ((hasFeature: boolean | Permission) => JSX.Element);
  className?: string;
}) => {
  const feature = useFeature(name);
  const hasFeature = feature?.active || false;
  return (
    <Collapse in={hasFeature} {...{ className }}>
      {typeof render === "function" ? render(hasFeature) : render}
    </Collapse>
  );
};
